import type { Configuration } from "@azure/msal-browser";

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined;
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined;
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE as string | undefined;

const redirectUri = typeof window !== "undefined" ? window.location.origin : "";
const postLogoutRedirectUri = redirectUri;

/**
 * Whether the required Microsoft Entra ID environment variables are configured.
 * Required: VITE_ENTRA_CLIENT_ID, VITE_ENTRA_TENANT_ID
 */
export const isAuthConfigured = !!(clientId && tenantId);

/**
 * Authority URL for Microsoft Entra ID.
 * - Single tenant: https://login.microsoftonline.com/{tenant-id}
 * - Multi-tenant: https://login.microsoftonline.com/common
 * - Organizations only: https://login.microsoftonline.com/organizations
 */
function getAuthority(): string {
  if (!tenantId) {
    return "https://login.microsoftonline.com/common";
  }
  return `https://login.microsoftonline.com/${tenantId}`;
}

/**
 * MSAL configuration for Microsoft Entra ID App Registration (SPA, Authorization Code Flow with PKCE).
 * Requires env: VITE_ENTRA_CLIENT_ID, VITE_ENTRA_TENANT_ID.
 * For backend API calls to include the JWT access token, VITE_ENTRA_API_SCOPE must be set
 * to your backend API scope (e.g. api://{api-app-id}/.default or api://{api-app-id}/access_as_user).
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: clientId || "00000000-0000-0000-0000-000000000000", // Placeholder when not configured
    authority: getAuthority(),
    redirectUri,
    postLogoutRedirectUri,
  },
  cache: {
    cacheLocation: "sessionStorage",
  },
};

/** Default scopes for sign-in (OIDC + optional API scope). */
export const loginRequest = {
  scopes: ["openid", "profile", "offline_access", ...(apiScope ? [apiScope] : [])],
};

/** Scopes for acquiring access token for the backend API. Required for Authorization: Bearer to be sent. */
export const apiRequest = apiScope ? { scopes: [apiScope] } : undefined;
